import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { type BreadcrumbItem, Breadcrumbs } from '~/ui/core/breadcrumbs/Breadcrumbs';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Tile } from '~/ui/core/tile/Tile';

import { useJobArchitectureItemPath } from '../api/use-job-architecture-item-path';
import { ErrorContent } from '../components/ErrorContent';
import { Title } from '../components/SidepanelTitle';
import { SkeletonBreadcrumbs } from '../components/SkeletonLoaders';
import { useProfileBreadcrumbs } from '../hooks/use-profile-breadcrumbs';
import { getTranslatedJobItemTitle } from '../profile-comparison/build-profile-comparison';
import {
    ComparisonHeader,
    ComparisonLoadingTile,
    ProfileComparisonTable,
} from '../profile-comparison/ProfileComparisonTable';
import { useComparisonProficiencySettings } from '../profile-comparison/use-comparison-proficiency-settings';
import { ActiveLanguageProvider, useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { useNavigateToStandaloneProfile } from '../url-navigation/use-navigate-to-standalone-profile';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { ComparisonLevelHeader } from './ComparisonLevelHeader';
import { type ComparedLevel, useRoleLevelComparisonData } from './use-role-level-comparison-data';

const ComparisonLevelHeaderCell: FunctionComponent<{
    readonly comparedLevel: ComparedLevel;
    readonly onOpen: (level: JobLevelDetails) => void;
    readonly onSelectVersion: (levelId: string, version: string) => void;
}> = ({ comparedLevel, onOpen, onSelectVersion }) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const { details, error, isFetching, level, versions } = comparedLevel;

    if (!is.undefined(details) && !is.undefined(versions)) {
        return (
            <ComparisonLevelHeader
                isFetching={isFetching}
                level={details}
                title={getTranslatedJobItemTitle(details, activeLanguage)}
                versions={versions}
                onOpen={() => onOpen(details)}
                onSelectVersion={(version) => onSelectVersion(level.id, version)}
            />
        );
    }

    if (is.null(error)) {
        return (
            <div
                className="flex min-w-0 w-full flex-col gap-s py-s"
                data-testid={`comparison-level-header-${level.id}`}
                aria-busy={true}
            >
                <SkeletonBase className="h-l w-3/4" />
                <SkeletonBase className="h-xl w-1/2" />
            </div>
        );
    }

    return (
        <div
            className="flex min-w-0 w-full items-center gap-xs py-s text-body-xs text-danger"
            role="alert"
            title={error.message}
        >
            {translate('pages.job-architecture-v2.job-profile-error-prefix')}
        </div>
    );
};

const ComparisonContent: FunctionComponent<{
    readonly architectureId: string;
    readonly comparedLevels: readonly ComparedLevel[];
    readonly onClose: () => void;
    readonly onSelectLevelVersion: (levelId: string, version: string) => void;
    readonly openProfilesOnStandalonePage: boolean;
    readonly role: JobRoleDetails;
    readonly showProficiency: boolean | undefined;
}> = ({
    architectureId,
    comparedLevels,
    onClose,
    onSelectLevelVersion,
    openProfilesOnStandalonePage,
    role,
    showProficiency,
}) => {
    const translate = useTranslate();
    const { activeLanguage, setActiveLanguage } = useActiveLanguage();
    const {
        openJobProfileTile,
        openSkillTile,
        updateLevelComparisonTileLanguage,
        updateLevelComparisonTileProficiency,
    } = useJobArchitectureUrlTileActions();
    const { navigate: navigateToStandaloneProfile } = useNavigateToStandaloneProfile();
    const proficiencySettings = useComparisonProficiencySettings({
        tileShowProficiency: showProficiency,
        updateTileProficiency: updateLevelComparisonTileProficiency,
    });
    const comparisonTitle = translate('pages.job-architecture-v2.job-profile-compare-levels');
    const roleTitle = getTranslatedJobItemTitle(role, activeLanguage);
    const { data: itemPath, isLoading: isItemPathLoading } = useJobArchitectureItemPath(architectureId, role.id);
    const { breadcrumbItems, isLoading: isBreadcrumbsLoading } = useProfileBreadcrumbs({
        architectureId,
        clusterId: itemPath?.cluster ?? '',
        currentItemTitle: roleTitle,
        familyId: itemPath?.family ?? '',
    });

    const handleLanguageChange = useCallback(
        (language: LanguageCodeForJSA): void => {
            setActiveLanguage(language);
            updateLevelComparisonTileLanguage(language);
        },
        [setActiveLanguage, updateLevelComparisonTileLanguage],
    );

    const openProfile = useCallback(
        (item: JobLevelDetails | JobRoleDetails): void => {
            if (openProfilesOnStandalonePage) {
                navigateToStandaloneProfile({
                    architectureId,
                    itemId: item.id,
                    itemType: item.type,
                    preferredLanguage: activeLanguage,
                    version: item.versionInfo?.version,
                });

                return;
            }

            openJobProfileTile({
                mode: 'view',
                preferredLanguage: activeLanguage,
                profileId: item.id,
                type: item.type,
                version: item.versionInfo?.version,
            });
        },
        [activeLanguage, architectureId, navigateToStandaloneProfile, openJobProfileTile, openProfilesOnStandalonePage],
    );
    const comparisonBreadcrumbItems: readonly BreadcrumbItem[] = breadcrumbItems.map((item, index) =>
        index === breadcrumbItems.length - 1 ? { ...item, onClick: () => openProfile(role) } : item,
    );

    return (
        <Tile className="gap-0" data-testid="role-level-comparison">
            <div className="flex w-full shrink-0 flex-col gap-s px-6xl pb-xxl pt-tile-edge">
                <ComparisonHeader
                    proficiencySettings={proficiencySettings}
                    onClose={onClose}
                    onLanguageChange={handleLanguageChange}
                />
                {isItemPathLoading || isBreadcrumbsLoading ? (
                    <SkeletonBreadcrumbs />
                ) : (
                    <Breadcrumbs items={comparisonBreadcrumbItems} lastItemIsCurrent={false} />
                )}
                <Title>{comparisonTitle}</Title>
            </div>
            <div className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-hidden px-6xl pb-xxl">
                <ProfileComparisonTable
                    columns={comparedLevels}
                    emptyMessage={translate('pages.job-architecture-v2.job-profile-level-comparison-no-skills')}
                    label={comparisonTitle}
                    proficiencySettings={proficiencySettings}
                    renderHeaderCell={(column) => {
                        const comparedLevel = comparedLevels.find(({ id }) => id === column.id);

                        return is.undefined(comparedLevel) ? null : (
                            <ComparisonLevelHeaderCell
                                comparedLevel={comparedLevel}
                                onOpen={openProfile}
                                onSelectVersion={onSelectLevelVersion}
                            />
                        );
                    }}
                    onOpenSkill={(skillId) => openSkillTile(skillId, activeLanguage)}
                />
            </div>
        </Tile>
    );
};

export const RoleLevelComparison: FunctionComponent<{
    readonly architectureId: string;
    readonly openProfilesOnStandalonePage: boolean;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly roleId: string;
    readonly showProficiency?: boolean;
    readonly onClose: () => void;
}> = ({ architectureId, onClose, openProfilesOnStandalonePage, preferredLanguage, roleId, showProficiency }) => {
    const { data, error, isLoading, selectLevelVersion } = useRoleLevelComparisonData(architectureId, roleId);
    const profiles = useMemo(
        () => [data?.role, ...(data?.levels ?? []).flatMap(({ details }) => (is.undefined(details) ? [] : [details]))],
        [data],
    );

    if (isLoading) {
        return <ComparisonLoadingTile testId="role-level-comparison-loading" onClose={onClose} />;
    }

    if (!is.null(error) || is.undefined(data)) {
        return (
            <Tile>
                <div className="w-full px-6xl">
                    <ErrorContent error={error} onClose={onClose} />
                </div>
            </Tile>
        );
    }

    return (
        <ActiveLanguageProvider preferredLanguage={preferredLanguage} profiles={profiles}>
            <ComparisonContent
                architectureId={architectureId}
                comparedLevels={data.levels}
                openProfilesOnStandalonePage={openProfilesOnStandalonePage}
                role={data.role}
                showProficiency={showProficiency}
                onClose={onClose}
                onSelectLevelVersion={selectLevelVersion}
            />
        </ActiveLanguageProvider>
    );
};
